// V2 admin_dashboard_northgate_park. Compact at-a-glance grid: hardware
// health, recent activity, tenant count, employee count. The mode-specific
// variants (commercial_office_focus / single_tenant_focus) render the
// same data with different hero copy — driven by site_mode.
//
// Redesign (2026-06-12): attention strip, linked KPI tiles, filtered
// activity feed, compact hardware strip, tenant overview panel.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon, Spinner } from '@ng-unifi/ui';
import { apiClient, ApiError } from '../lib/api.js';
import { getAllEmployees } from '../lib/employees.js';
import { useSiteMode } from '../auth/SiteModeContext.js';
import { useAuth } from '../auth/AuthContext.js';
import { useTenantScope } from '../auth/TenantScopeContext.js';
import {
  auditCodeTone,
  formatAuditSummary,
  formatCompactAuditSummary,
  isDashboardNoise,
} from '../lib/auditLabels.js';
import { resourceTypeLabel } from '../lib/humanLabels.js';
import { AttentionStrip } from '../components/AttentionStrip.js';
import type { AttentionAlert } from '../components/AttentionStrip.js';

interface DeviceRow {
  id: string;
  name: string;
  status: 'online' | 'upgrading' | 'offline' | 'unknown';
  label_override: string | null;
}
interface AuditRow {
  id: string;
  action: string;
  resource_type: string;
  resource_id: string | null;
  actor_user_id: string | null;
  actor_name?: string | null;
  tenant_name?: string | null;
  created_at: string;
}
interface MasterInventoryDoor {
  id: string;
  name: string;
  full_name?: string | null;
  status?: 'online' | 'offline' | 'unknown';
  online?: boolean;
}
interface MasterInventoryDevice {
  id: string;
  name: string;
  status?: DeviceRow['status'];
}
interface MasterInventoryResponse {
  data: {
    doors: MasterInventoryDoor[];
    devices: MasterInventoryDevice[];
  };
}
interface TenantRow {
  id: string;
  name: string;
  status?: string | null;
  employee_count?: number | null;
  fob_count?: number | null;
  pin_count?: number | null;
  mobile_count?: number | null;
}
/**
 * GET /api/v1/admin/outbox returns counts grouped by job state, so a state
 * with no jobs is simply absent rather than zero.
 */
interface OutboxSummary {
  totals: Partial<Record<'pending' | 'running' | 'succeeded' | 'failed' | 'dead_letter', number>>;
}
interface JourneyEmployee {
  id: string;
  tenant_id?: string | null;
  tenant_name?: string | null;
  name: string;
  email: string;
  status: string;
  unifi_user_id: string | null;
}
interface JourneyEvent {
  id: string;
  type: string;
  occurred_at: string;
  severity: string;
  door_name?: string | null;
  unifi_device_id: string | null;
  unifi_door_id?: string | null;
  tenant_id?: string | null;
  employee?: JourneyEmployee | null;
}
interface JourneyResponse {
  data: {
    events: JourneyEvent[];
  };
}

type SystemHealth = {
  status: 'healthy' | 'degraded' | 'offline';
  label: string;
  detail: string;
  icon: string;
  iconTone: string;
};

// GAP-D8 / GAP-D9 (audit 2026-05-21): titles used to collide — both
// commercial_multi_tenant and multi_family rendered "Property overview".
// The commercial subtitle also leaked the internal word "Slices" into
// operator UI. New titles match the operator's mental model per mode.
const HERO_BY_MODE: Record<string, { title: string; subtitle: string }> = {
  commercial_multi_tenant: {
    title: 'Portfolio overview',
    subtitle: 'Tenants, hardware health, recent activity.',
  },
  single_tenant: {
    title: 'Workplace overview',
    subtitle: 'Employees, credentials, recent activity.',
  },
  multi_family: {
    title: 'Property overview',
    subtitle: 'Buildings, residents, recent activity.',
  },
};

// UX-017: these resolved absent data two different ways, and both were wrong.
// A door whose status the controller reports as 'unknown' — a value the
// interface below declares — became 'offline', which then raised the red
// "N doors offline" alert on the dashboard. A device with no status at all
// became 'online', so an unreachable device counted as healthy. Unknown is now
// its own state and is never counted as either.
function masterDoorStatus(door: MasterInventoryDoor): DeviceRow['status'] {
  if (door.status === 'online' || door.online === true) return 'online';
  if (door.status === 'offline' || door.online === false) return 'offline';
  return 'unknown';
}

function masterDeviceStatus(status: MasterInventoryDevice['status']): DeviceRow['status'] {
  if (status === 'offline' || status === 'upgrading' || status === 'online') return status;
  return 'unknown';
}

async function loadMasterHardware(): Promise<DeviceRow[]> {
  const res = await apiClient.get<MasterInventoryResponse>(
    '/api/v1/admin/hardware/inventory',
    { skipTenantScope: true },
  );
  return [
    ...res.data.doors.map((door) => ({
      id: `door:${door.id}`,
      name: door.full_name ?? door.name,
      status: masterDoorStatus(door),
      label_override: null,
    })),
    ...res.data.devices.map((device) => ({
      id: `device:${device.id}`,
      name: device.name,
      status: masterDeviceStatus(device.status),
      label_override: null,
    })),
  ];
}

function buildAttentionAlerts(
  devices: DeviceRow[] | null,
  outboxTotals: OutboxSummary | null,
): AttentionAlert[] {
  const alerts: AttentionAlert[] = [];

  const offlineDevices = devices?.filter((d) => d.status === 'offline') ?? [];
  if (offlineDevices.length > 0) {
    alerts.push({
      key: 'offline-devices',
      tone: 'red',
      title: `${offlineDevices.length} door${offlineDevices.length === 1 ? '' : 's'} offline`,
      detail: offlineDevices.slice(0, 2).map((d) => d.name ?? d.id).join(' · '),
      href: '/hardware',
    });
  }

  // Reported separately and in a lower tone: an unknown reading is a gap in
  // what we can see, not a device that is down.
  const unknownDevices = devices?.filter((d) => d.status === 'unknown') ?? [];
  if (unknownDevices.length > 0) {
    alerts.push({
      key: 'unknown-devices',
      tone: 'amber',
      title: `${unknownDevices.length} device${unknownDevices.length === 1 ? '' : 's'} not reporting status`,
      detail: 'The controller did not say whether these are online.',
      href: '/hardware',
    });
  }

  const failedJobs = outboxTotals?.totals?.failed ?? 0;
  if (failedJobs > 0) {
    alerts.push({
      key: 'failed-jobs',
      tone: 'amber',
      title: `${failedJobs} sync job${failedJobs === 1 ? '' : 's'} need attention`,
      // UX-004: this used to point at /admin/settings, which is not a route —
      // the catch-all bounced the operator back to the dashboard they came
      // from. The failed jobs live in the outbox; `state` is read from the URL
      // there so the list opens already filtered.
      detail: 'Open the sync queue to retry or dismiss them',
      href: '/outbox?state=failed',
    });
  }

  return alerts;
}

export function AdminDashboardPage() {
  const { state } = useAuth();
  const { tenantId } = useTenantScope();
  const [devices, setDevices] = useState<DeviceRow[] | null>(null);
  const [audit, setAudit] = useState<AuditRow[] | null>(null);
  const [accessEvents, setAccessEvents] = useState<JourneyEvent[] | null>(null);
  const [tenants, setTenants] = useState<TenantRow[] | null>(null);
  const [tenantCount, setTenantCount] = useState<number | null>(null);
  const [employeeCount, setEmployeeCount] = useState<number | null>(null);
  // GAP-D2 (audit 2026-05-21): multi_family needs Residents + occupied
  // Units instead of Tenants/Employees on the KPI strip. Tenants tile is
  // suppressed in single_tenant entirely.
  const [residentCount, setResidentCount] = useState<number | null>(null);
  const [residentsErr, setResidentsErr] = useState<ApiError | null>(null);
  // UX-018: the single_tenant "Key fobs" tile passed a hardcoded null for both
  // value and error, so it rendered "—" forever and could never report a
  // failure either. Count active nfc_card credentials from the inventory the
  // master credential pages already use.
  const [fobCount, setFobCount] = useState<number | null>(null);
  const [fobsErr, setFobsErr] = useState<ApiError | null>(null);
  const [occupiedUnits, setOccupiedUnits] = useState<number | null>(null);
  const [unitsErr, setUnitsErr] = useState<ApiError | null>(null);
  const [outboxTotals, setOutboxTotals] = useState<OutboxSummary | null>(null);
  const [outboxErr, setOutboxErr] = useState<ApiError | null>(null);

  // Per-panel error state. We DON'T swallow 401/403/404/502 — masking a
  // permission/controller failure as "no records" is exactly the trap the
  // reviewer flagged (Tranche V #4).
  const [devicesErr, setDevicesErr] = useState<ApiError | null>(null);
  const [auditErr, setAuditErr] = useState<ApiError | null>(null);
  const [accessErr, setAccessErr] = useState<ApiError | null>(null);
  const [tenantsErr, setTenantsErr] = useState<ApiError | null>(null);
  const [employeesErr, setEmployeesErr] = useState<ApiError | null>(null);

  // PR #53 review follow-up: read the install's mode from the
  // SiteModeContext that App.tsx wires once at the top, instead of
  // fetching /api/v1/setup/status on every dashboard mount. Avoids the
  // duplicate request and lets the mode-conditional fetches fire
  // synchronously on first render.
  const siteMode = useSiteMode();
  const isMaster = state.status === 'authed' && state.user.role === 'master_admin';
  const masterWithoutTenantScope =
    isMaster && siteMode === 'commercial_multi_tenant' && !tenantId;

  useEffect(() => {
    let cancelled = false;

    async function loadInto<T>(
      url: string,
      setData: (d: T[]) => void,
      setErr: (e: ApiError | null) => void,
      load?: () => Promise<{ data: T[] }>,
    ) {
      try {
        const res = load ? await load() : await apiClient.get<{ data: T[] }>(url);
        if (!cancelled) {
          setData(res.data);
          setErr(null);
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) setErr(err);
        else setErr(new ApiError('Network failure', 0, 'network'));
      }
    }

    // Always-applicable loads.
    if (isMaster) {
      void loadMasterHardware()
        .then((rows) => {
          if (!cancelled) {
            setDevices(rows);
            setDevicesErr(null);
          }
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          if (err instanceof ApiError) setDevicesErr(err);
          else setDevicesErr(new ApiError('Network failure', 0, 'network'));
        });
    }
    void loadInto<AuditRow>('/api/v1/audit?limit=8', setAudit, setAuditErr);
    void apiClient
      .get<JourneyResponse>(
        '/api/v1/events/journey?limit=10',
        masterWithoutTenantScope ? { skipTenantScope: true } : undefined,
      )
      .then((res) => {
        if (!cancelled) {
          setAccessEvents(res.data.events);
          setAccessErr(null);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setAccessEvents([]);
        if (err instanceof ApiError) setAccessErr(err);
        else setAccessErr(new ApiError('Network failure', 0, 'network'));
      });

    // UX-010: this failure used to be swallowed. outboxTotals stayed null,
    // which reads as "no failed jobs", and the health tile then told the
    // operator the sync queue was responding. A check we could not run is not
    // a check that passed.
    // The summary endpoint is master-only, so for anyone else this is not a
    // failure — it is simply not part of their view.
    if (isMaster) {
      // The URL was `/api/v1/admin/outbox/summary`, which has never existed —
      // the route is `/admin/outbox`. Because the old code swallowed the
      // failure, the 404 was invisible for as long as it had been wrong.
      // Making the failure visible (UX-010) is what exposed it: every
      // dashboard started reporting "sync queue did not respond", which was
      // true, and had always been true.
      apiClient
        .get<{ data: OutboxSummary }>('/api/v1/admin/outbox', {
          skipTenantScope: true,
        })
        .then((res) => {
          if (!cancelled) {
            setOutboxTotals(res.data);
            setOutboxErr(null);
          }
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          setOutboxErr(err instanceof ApiError ? err : new ApiError('Network failure', 0, 'network'));
        });
    }

    // Mode-dependent KPI tiles only fire the fetches their target mode
    // needs. /api/v1/residents and /api/v1/households 404 outside
    // multi_family; /api/v1/tenants is master-only and only meaningful
    // in commercial.
    if (siteMode === 'commercial_multi_tenant') {
      if (isMaster) {
        void loadInto<TenantRow>(
          '/api/v1/tenants',
          (rows) => {
            setTenantCount(rows.length);
            setTenants(rows);
            if (tenantId) {
              const selected = rows.find((row) => row.id === tenantId);
              setEmployeeCount(selected?.employee_count ?? 0);
            } else {
              setEmployeeCount(
                rows.reduce((sum, row) => sum + (row.employee_count ?? 0), 0),
              );
            }
            setEmployeesErr(null);
          },
          setTenantsErr,
        );
      } else {
        void loadInto<unknown>(
          '/api/v1/employees',
          (rows) => setEmployeeCount(rows.length),
          setEmployeesErr,
          () => getAllEmployees<unknown>(),
        );
      }
    } else if (siteMode === 'single_tenant') {
      void loadInto<unknown>(
        '/api/v1/employees',
        (rows) => setEmployeeCount(rows.length),
        setEmployeesErr,
      );
      // The inventory endpoint is master-only. Tenant operators in a
      // single_tenant install cannot reach /admin/credentials either, so the
      // tile is not rendered for them at all rather than shown empty.
      if (isMaster) {
        void apiClient
          .get<{ data: Array<{ type: string; status: string }> }>(
            '/api/v1/admin/credentials/inventory',
            { skipTenantScope: true },
          )
          .then((res) => {
            if (cancelled) return;
            setFobCount(
              res.data.filter((c) => c.type === 'nfc_card' && c.status === 'active').length,
            );
            setFobsErr(null);
          })
          .catch((err: unknown) => {
            if (cancelled) return;
            setFobsErr(
              err instanceof ApiError ? err : new ApiError('Network failure', 0, 'network'),
            );
          });
      }
    } else if (siteMode === 'multi_family') {
      void loadInto<unknown>(
        '/api/v1/residents',
        (rows) => setResidentCount(rows.length),
        setResidentsErr,
      );
      void loadInto<{ moved_out_at: string | null }>(
        '/api/v1/households',
        (rows) => setOccupiedUnits(rows.filter((h) => h.moved_out_at == null).length),
        setUnitsErr,
      );
    }

    return () => {
      cancelled = true;
    };
  }, [siteMode, isMaster, masterWithoutTenantScope, tenantId]);

  // siteMode is constrained to one of three string literals by the API
  // type. HERO_BY_MODE is a closed code-defined map. Not an injection sink.
  // eslint-disable-next-line security/detect-object-injection
  const hero = HERO_BY_MODE[siteMode] ?? HERO_BY_MODE.commercial_multi_tenant!;

  // Zone 1: build attention alerts from current data.
  const attentionAlerts = buildAttentionAlerts(devices, outboxTotals);

  // Zone 3: filter noise from audit feed.
  const filteredAudit = audit?.filter((row) => !isDashboardNoise(row.action)) ?? null;
  const offlineDevices = devices?.filter((d) => d.status === 'offline') ?? [];
  const unknownDevices = devices?.filter((d) => d.status === 'unknown') ?? [];
  const systemHealth = useMemo(
    () => computeSystemHealth({
      devices,
      devicesErr,
      accessErr,
      tenantsErr,
      employeesErr,
      offlineDevices,
      unknownDevices,
      outboxTotals,
      outboxErr,
      // Hardware inventory and the outbox summary are master-only endpoints.
      // For anyone else their absence is the shape of the role, not a fault.
      canSeeHardware: isMaster,
    }),
    [
      accessErr,
      devices,
      devicesErr,
      employeesErr,
      isMaster,
      offlineDevices,
      outboxErr,
      outboxTotals,
      tenantsErr,
      unknownDevices,
    ],
  );

  return (
    <div className="space-y-6">
      {/* Zone 1: Needs attention strip */}
      <AttentionStrip alerts={attentionAlerts} />

      <header>
        <h1 className="font-headline-md text-headline-md text-on-surface">{hero.title}</h1>
        <p className="font-body-md text-body-md text-on-surface-variant">{hero.subtitle}</p>
      </header>

      {/* Zone 2: useful operating summary */}
      <section className="grid gap-4 md:grid-cols-3">
        <SystemHealthTile health={systemHealth} />
        {/* Mode-defining tiles in slot 1 + 2 */}
        {siteMode === 'commercial_multi_tenant' ? (
          <>
            <Link to="/tenants" className="block">
              <StatTile
                icon="apartment"
                label="Tenants"
                value={tenantsErr ? null : tenantCount}
                error={tenantsErr}
              />
            </Link>
            <Link to="/employees" className="block">
              <StatTile
                icon="badge"
                label="Employees"
                value={employeesErr ? null : employeeCount}
                error={employeesErr}
              />
            </Link>
          </>
        ) : siteMode === 'multi_family' ? (
          <>
            <Link to="/residents" className="block">
              <StatTile
                icon="people"
                label="Residents"
                value={residentsErr ? null : residentCount}
                error={residentsErr}
              />
            </Link>
            <Link to="/units" className="block">
              <StatTile
                icon="home"
                label="Units occupied"
                value={unitsErr ? null : occupiedUnits}
                error={unitsErr}
              />
            </Link>
          </>
        ) : (
          // single_tenant: no Tenants tile (operator brief at /setup
          // promises "tenant segmentation is hidden"). Keep Employees
          // and add Credentials tile.
          <>
            <Link to="/employees" className="block">
              <StatTile
                icon="badge"
                label="Employees"
                value={employeesErr ? null : employeeCount}
                error={employeesErr}
              />
            </Link>
            {isMaster ? (
              <Link to="/admin/credentials" className="block">
                <StatTile
                  icon="key"
                  label="Key fobs"
                  value={fobsErr ? null : fobCount}
                  error={fobsErr}
                />
              </Link>
            ) : null}
          </>
        )}
      </section>

      <section className="grid gap-6 grid-cols-1 lg:grid-cols-2">
        {/* Zone 3: Recent access feed */}
        <Panel title="Recent access" icon="history">
          {accessErr ? (
            <ErrorBlock err={accessErr} hint="Access events unavailable for this scope." />
          ) : accessEvents == null ? (
            <Spinner />
          ) : accessEvents.length === 0 ? (
            <p className="text-on-surface-variant font-body-md text-body-md">
              No recent door access recorded.
            </p>
          ) : (
            <>
              <ul className="divide-y divide-outline-variant">
                {accessEvents.slice(0, 10).map((event) => (
                  <RecentAccessRow key={event.id} event={event} />
                ))}
              </ul>
              <div className="mt-3 pt-3 border-t border-outline-variant">
                <Link
                  to="/journeys"
                  className="font-body-md text-body-md text-primary hover:underline"
                >
                  Open employee journeys →
                </Link>
              </div>
            </>
          )}
        </Panel>

        <Panel title="Management Portal Activity" icon="manage_history">
          {auditErr ? (
            <ErrorBlock err={auditErr} hint="Audit log unavailable for this scope." />
          ) : filteredAudit == null ? (
            <Spinner />
          ) : filteredAudit.length === 0 ? (
            <p className="text-on-surface-variant font-body-md text-body-md">
              No portal changes recorded.
            </p>
          ) : (
            <AuditActivityList rows={filteredAudit} />
          )}
        </Panel>
      </section>

      {/* Zone 5: Tenant Overview panel (commercial mode only) */}
      {/* UX-020: gated on `tenants` being truthy, so a failed load removed the
          panel entirely — the operator saw nothing at all, and the ErrorBlock
          inside was dead code. Render on error too, and let it explain. */}
      {siteMode === 'commercial_multi_tenant' && isMaster && (tenants || tenantsErr) && (
        <Panel title="Tenant overview" icon="apartment">
          {tenantsErr ? (
            <ErrorBlock err={tenantsErr} hint="Tenants unavailable for this scope." />
          ) : (
            <>
              <div className="max-h-[28rem] divide-y divide-outline-variant overflow-y-auto">
                {(tenants ?? []).map((t) => (
                  <Link
                    key={t.id}
                    to={`/tenants/${t.id}`}
                    className="flex items-center justify-between gap-3 py-3 hover:bg-surface-container-low"
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-body-md text-body-md font-medium text-on-surface">
                        {t.name}
                      </span>
                      <span className="font-body-sm text-body-sm text-on-surface-variant">
                        {t.status ?? 'active'}
                      </span>
                    </span>
                    <span className="shrink-0 rounded-full bg-surface-container px-2 py-1 font-label-md text-label-md text-on-surface-variant">
                      {t.employee_count ?? 0} employee{(t.employee_count ?? 0) === 1 ? '' : 's'}
                    </span>
                  </Link>
                ))}
              </div>
              <Link to="/tenants" className="mt-3 block text-sm text-primary hover:underline">
                Manage tenants →
              </Link>
            </>
          )}
        </Panel>
      )}
    </div>
  );
}

/**
 * The health verdict may only claim what it actually checked.
 *
 * UX-009: hardware inventory is fetched only for masters, so for every tenant
 * operator `devices` stayed null forever and this returned "Checking system /
 * Loading controller and portal status" permanently — a spinner that was never
 * going to finish.
 *
 * UX-010: the healthy branch asserted "Controller, sync queue, and activity
 * feed are responding" while the sync-queue request had been caught and
 * discarded. A check that could not run now degrades the verdict and is named.
 */
function computeSystemHealth({
  devices,
  devicesErr,
  accessErr,
  tenantsErr,
  employeesErr,
  offlineDevices,
  unknownDevices,
  outboxTotals,
  outboxErr,
  canSeeHardware,
}: {
  devices: DeviceRow[] | null;
  devicesErr: ApiError | null;
  accessErr: ApiError | null;
  tenantsErr: ApiError | null;
  employeesErr: ApiError | null;
  offlineDevices: DeviceRow[];
  unknownDevices: DeviceRow[];
  outboxTotals: OutboxSummary | null;
  outboxErr: ApiError | null;
  canSeeHardware: boolean;
}): SystemHealth {
  if (canSeeHardware && devicesErr) {
    return {
      status: 'offline',
      label: 'Controller unreachable',
      detail: 'Hardware inventory is unavailable. Door state shown elsewhere is last-known.',
      icon: 'error',
      iconTone: 'text-error',
    };
  }

  const problems = [
    offlineDevices.length > 0
      ? `${offlineDevices.length} offline device${offlineDevices.length === 1 ? '' : 's'}`
      : null,
    (outboxTotals?.totals?.failed ?? 0) > 0
      ? `${outboxTotals!.totals!.failed} failed sync job${outboxTotals!.totals!.failed === 1 ? '' : 's'}`
      : null,
  ].filter((issue): issue is string => issue !== null);

  if (problems.length > 0) {
    return {
      status: 'degraded',
      label: 'Needs attention',
      detail: problems.join(' · '),
      icon: 'warning',
      iconTone: 'text-error',
    };
  }

  // Checks that did not answer. These are gaps in visibility, not incidents,
  // and they must never be folded into a healthy verdict.
  const blind = [
    unknownDevices.length > 0
      ? `${unknownDevices.length} device${unknownDevices.length === 1 ? '' : 's'} not reporting status`
      : null,
    outboxErr ? 'sync queue did not respond' : null,
    accessErr ? 'access activity unavailable' : null,
    tenantsErr ? 'tenant list unavailable' : null,
    employeesErr ? 'employee count unavailable' : null,
  ].filter((issue): issue is string => issue !== null);

  if (blind.length > 0) {
    return {
      status: 'degraded',
      label: 'Partial visibility',
      detail: `${blind.slice(0, 2).join(' · ')}. Other checks are normal.`,
      icon: 'cloud_off',
      iconTone: 'text-tertiary-fixed-dim',
    };
  }

  if (canSeeHardware && devices === null) {
    return {
      status: 'degraded',
      label: 'Checking system',
      detail: 'Loading controller and portal status.',
      icon: 'sync',
      iconTone: 'text-on-surface-variant',
    };
  }

  if (!canSeeHardware) {
    return {
      status: 'healthy',
      label: 'Limited view',
      detail: 'Hardware and sync status are visible to site administrators. Portal activity is normal.',
      icon: 'visibility',
      iconTone: 'text-on-surface-variant',
    };
  }

  return {
    status: 'healthy',
    label: 'Operating normally',
    detail: 'Controller inventory, sync queue and activity feed all responded.',
    icon: 'check_circle',
    iconTone: 'text-success',
  };
}

function SystemHealthTile({ health }: { health: SystemHealth }) {
  return (
    <div className="rounded-lg border border-outline-variant bg-surface-container-lowest p-4 text-on-surface">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-surface-container">
          <Icon name={health.icon} className={`text-2xl ${health.iconTone}`} />
        </div>
        <div className="min-w-0">
          <div className="font-label-md text-label-md uppercase text-on-surface-variant">
            System status
          </div>
          <div className="font-headline-sm text-headline-sm">{health.label}</div>
          <div className="mt-1 font-body-sm text-body-sm text-on-surface-variant">
            {health.detail}
          </div>
        </div>
      </div>
    </div>
  );
}

function accessEventLabel(type: string): string {
  const known = new Map<string, string>([
    ['access.authorized', 'Access granted'],
    ['access.denied', 'Access denied'],
    ['access.denied.invalid_credential', 'Credential denied'],
    ['access.denied.no_permission', 'Access denied'],
    ['door.unlock', 'Door unlocked'],
    ['door.lock', 'Door locked'],
    ['door.opened', 'Door opened'],
    ['door.closed', 'Door closed'],
    ['door.forced_open', 'Forced door open'],
    ['door.held_open', 'Door held open'],
  ]);
  const match = known.get(type);
  if (match) return match;
  const words = type.replace(/[._-]+/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function accessEventTone(event: JourneyEvent): string {
  const severity = event.severity.toLowerCase();
  const type = event.type.toLowerCase();
  if (severity === 'error' || severity === 'critical' || type.includes('denied') || type.includes('forced')) {
    return 'bg-error-container text-on-error-container';
  }
  if (severity === 'warning' || severity === 'warn' || type.includes('held')) {
    return 'bg-tertiary-fixed text-on-tertiary-fixed';
  }
  if (type.includes('authorized') || type.includes('unlock')) {
    return 'bg-secondary-container text-on-secondary-container';
  }
  return 'bg-surface-container text-on-surface-variant';
}

function locationLabel(event: JourneyEvent): string {
  if (event.door_name?.trim()) return event.door_name.trim();
  if (event.unifi_door_id) return `Unknown door (${event.unifi_door_id})`;
  if (event.unifi_device_id) return `Unknown reader (${event.unifi_device_id})`;
  return 'Unknown location';
}

function employeeHref(employee: JourneyEmployee | null | undefined): string | null {
  if (!employee) return null;
  return employee.tenant_id
    ? `/employees/${employee.id}?tenantId=${encodeURIComponent(employee.tenant_id)}`
    : `/employees/${employee.id}`;
}

function RecentAccessRow({ event }: { event: JourneyEvent }) {
  const employee = event.employee ?? null;
  const href = employeeHref(employee);
  const personLabel = employee
    ? employee.tenant_name
      ? `${employee.tenant_name} - ${employee.name}`
      : employee.name
    : 'Unknown person';
  return (
    <li className="py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {href ? (
            <Link
              // 21px tall with the timestamp line right under it, so it fails
              // 2.5.8's spacing exception and has to meet the size floor
              // itself. This is the primary link of every dashboard feed row.
              to={href}
              className="inline-flex min-h-[1.5rem] items-center font-body-md text-body-md font-medium text-on-surface hover:underline"
            >
              {personLabel}
            </Link>
          ) : (
            <span className="font-body-md text-body-md font-medium text-on-surface">{personLabel}</span>
          )}
          <div className="mt-1 font-body-sm text-body-sm text-on-surface-variant">
            {locationLabel(event)} · {new Date(event.occurred_at).toLocaleString()}
          </div>
        </div>
        {/* UX-012: these are journey/access events, not audit rows. They used
            to link to /activity/:id, which scans the audit list for a matching
            id and therefore always rendered "Audit entry not in the recent
            window". Physical access history belongs to /events/:id — the same
            destination JourneysPage uses. */}
        <Link
          to={`/events/${event.id}`}
          className={`shrink-0 rounded-full px-2 py-1 font-label-md text-label-md hover:underline ${accessEventTone(event)}`}
        >
          {accessEventLabel(event.type)}
        </Link>
      </div>
    </li>
  );
}

function AuditActivityList({ rows }: { rows: AuditRow[] }) {
  return (
    <>
      <ul className="divide-y divide-outline-variant text-sm">
        {rows.slice(0, 6).map((row) => {
          const tone = auditCodeTone(row.action);
          const label = formatCompactAuditSummary(row);
          return (
            <li key={row.id} className="py-2">
              <Link
                to={`/activity/${row.id}`}
                className={
                  tone === 'quiet'
                    ? 'block min-h-[1.5rem] font-body-md text-body-md text-on-surface-variant hover:underline'
                    : tone === 'warning'
                      ? 'block min-h-[1.5rem] font-body-md text-body-md text-error hover:underline'
                      : 'block min-h-[1.5rem] font-body-md text-body-md text-on-surface hover:underline'
                }
                title={formatAuditSummary(row)}
              >
                {label}
              </Link>
              <div className="font-label-md text-label-md text-on-surface-variant">
                {resourceTypeLabel(row.resource_type)} · {new Date(row.created_at).toLocaleString()}
              </div>
            </li>
          );
        })}
      </ul>
      <div className="mt-3 pt-3 border-t border-outline-variant">
        <Link to="/activity" className="font-body-md text-body-md text-primary hover:underline">
          See all activity →
        </Link>
      </div>
    </>
  );
}

function ErrorBlock({ err, hint }: { err: ApiError; hint?: string }) {
  // Distinct copy per status family so the operator can tell a missing
  // scope (403) from a controller outage (502) from a real bug (5xx).
  const family =
    err.status === 401 || err.status === 403
      ? 'You don\'t have access to this data in the current scope.'
      : err.status === 404
        ? 'Endpoint not available — likely setup not complete or wrong site mode.'
        : err.status >= 500
          ? 'Backend or controller error. Retry shortly.'
          : 'Request failed.';
  return (
    <div className="text-error font-body-md text-body-md">
      <div>{family}</div>
      <div className="font-label-md text-label-md text-on-surface-variant uppercase mt-1">
        {err.status} {err.code}
      </div>
      {hint ? <div className="text-on-surface-variant mt-1">{hint}</div> : null}
    </div>
  );
}

function StatTile({
  icon,
  label,
  value,
  error,
  tone = 'normal',
}: {
  icon: string;
  label: string;
  value: number | null;
  error?: ApiError | null;
  tone?: 'normal' | 'warning';
}) {
  // Error trumps tone — show a red mini-pill so the operator can't
  // mistake a permission failure for a real "0".
  if (error) tone = 'warning';
  return (
    <div className="bg-surface-container-lowest border border-outline-variant rounded-lg p-4 flex items-center gap-3 h-full">
      <div
        className={`inline-flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0 ${
          tone === 'warning'
            ? 'bg-error-container text-on-error-container'
            : 'bg-primary-container text-on-primary'
        }`}
      >
        <Icon name={icon} />
      </div>
      <div>
        <div className="font-label-md text-label-md text-on-surface-variant uppercase">
          {label}
        </div>
        <div className="font-headline-sm text-headline-sm text-on-surface">
          {error ? '⚠' : (value ?? '—')}
        </div>
        {error ? (
          <div className="font-label-md text-label-md text-error">
            {error.status} {error.code}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Panel({
  title,
  icon,
  children,
}: {
  title: string;
  icon: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-surface-container-lowest border border-outline-variant rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-outline-variant flex items-center gap-2">
        <Icon name={icon} className="text-on-surface-variant" />
        <h2 className="font-headline-sm text-headline-sm text-on-surface">{title}</h2>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}
